import React, { useCallback, useEffect, useRef, useState } from "react";
import { FolderTree } from "lucide-react";
import folderStructureIcon from "../assets/folder-structure.svg";
import noSizeLimitsIcon from "../assets/no-size-limits.svg";
import supportedFileUploadIcon from "../assets/supported-upload-files.svg";
import uploadFileIcon from "../assets/upload_file.svg";
import uploadIcon from "../assets/upload_popup.svg";
import { useToast } from "../context/ToastContext";
import { Colors } from "../utils/colors";
import {
	CollectedFolder,
	FailedEntry,
	collectDroppedEntries,
	collectFolderFiles,
} from "../utils/folderUpload";

interface FileSelectionProps {
	onFilesSelected: (files: File[]) => void;
}

const acceptedFileTypes = {
	"image/png": [".png"],
	"image/jpeg": [".jpg", ".jpeg"],
	"application/pdf": [".pdf"],
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
		".xlsx",
	],
	"application/vnd.ms-excel": [".xls"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
		".docx",
	],
	"application/msword": [".doc"],
	// R-1.2: document-shaped text sources (email threads / Slack exports / notes).
	"text/markdown": [".md", ".markdown"],
	"text/plain": [".txt"],
	"application/zip": [".zip"],
};
export const validateFileType = (file: File): boolean => {
	if (Object.keys(acceptedFileTypes).includes(file.type)) return true;
	const name = file.name.toLowerCase();
	return [
		".png",
		".jpg",
		".jpeg",
		".pdf",
		".xls",
		".xlsx",
		".doc",
		".docx",
		".md",
		".markdown",
		".txt",
		".zip",
	].some((ext) => name.endsWith(ext));
};

const UNSUPPORTED_TYPES_MESSAGE =
	"Please upload supported file types (PNG, PDF, XLSX, DOC, DOCX, MD, TXT, ZIP).";

const FileSelection: React.FC<FileSelectionProps> = ({ onFilesSelected }) => {
	const { showToast } = useToast();
	const [isDragOver, setIsDragOver] = useState(false);
	// Count preview for a picked folder — shown before the upload starts so the
	// user can see what was collected (and what was skipped) and confirm.
	const [folderPreview, setFolderPreview] = useState<CollectedFolder | null>(
		null
	);
	// Unreadable entries must be acknowledged before the (incomplete) selection
	// can be uploaded.
	const [failuresAcknowledged, setFailuresAcknowledged] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const folderInputRef = useRef<HTMLInputElement>(null);

	// `webkitdirectory` is non-standard and absent from React's input typings,
	// so it is set on the DOM node directly rather than through a cast.
	useEffect(() => {
		const input = folderInputRef.current;
		if (!input) return;
		input.setAttribute("webkitdirectory", "");
		input.setAttribute("directory", "");
	}, []);

	const handleFiles = useCallback(
		(files: FileList | null) => {
			if (!files) return;

			const validFiles = Array.from(files).filter(validateFileType);
			if (validFiles.length === 0) {
				showToast(UNSUPPORTED_TYPES_MESSAGE, "info");
				return;
			}

			onFilesSelected(validFiles);
		},
		[onFilesSelected, showToast]
	);

	/**
	 * Stage a directory tree: strip the local folder name (the project name is
	 * the root), drop junk and unsupported files, then show the count preview.
	 */
	const handleFolder = useCallback(
		(
			files: File[],
			pathFor?: (file: File) => string,
			failed: FailedEntry[] = []
		) => {
			const collected = collectFolderFiles(
				files,
				validateFileType,
				pathFor,
				failed
			);
			if (collected.files.length === 0 && collected.failed.length === 0) {
				showToast(UNSUPPORTED_TYPES_MESSAGE, "info");
				return;
			}
			setFailuresAcknowledged(false);
			setFolderPreview(collected);
		},
		[showToast]
	);

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(true);
	};

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragOver(false);

			// A dropped *folder* only exposes its tree through the entry API;
			// `dataTransfer.files` would flatten it. Snapshot the DataTransfer
			// before awaiting — it is neutered once the event handler returns.
			const dataTransfer = e.dataTransfer;
			const droppedFiles = dataTransfer.files;
			void collectDroppedEntries(dataTransfer)
				.then((traversal) => {
					// `null` is the ONLY signal that this drop holds no directory —
					// the plain-file path is correct then, and only then.
					if (traversal === null) {
						handleFiles(droppedFiles);
						return;
					}
					const paths = new Map(
						traversal.entries.map(({ file, fullPath }) => [file, fullPath])
					);
					handleFolder(
						traversal.entries.map(({ file }) => file),
						(file) => paths.get(file) ?? file.name,
						traversal.failed
					);
				})
				.catch((err) => {
					// Fail CLOSED. Falling back to `dataTransfer.files` here would
					// flatten a folder the user believes they dropped whole, losing
					// every sub-path silently. Surface it as a failed traversal so the
					// acknowledgement gate applies.
					handleFolder([], undefined, [
						{
							path: "(dropped folder)",
							reason:
								err instanceof Error
									? `could not be read — ${err.message}`
									: "could not be read",
						},
					]);
				});
		},
		[handleFiles, handleFolder]
	);

	const handleFileInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			handleFiles(e.target.files);
		},
		[handleFiles]
	);

	const handleFolderInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			handleFolder(Array.from(e.target.files ?? []));
			// Allow re-picking the same folder after cancelling the preview.
			e.target.value = "";
		},
		[handleFolder]
	);

	const handleUploadClick = () => {
		fileInputRef.current?.click();
	};

	const handleFolderClick = () => {
		folderInputRef.current?.click();
	};

	const blockingCollisions = folderPreview?.collisions ?? [];
	const unreadEntries = folderPreview?.failed ?? [];
	const unstorablePaths = folderPreview?.unstorable ?? [];
	const canConfirmFolder =
		!!folderPreview &&
		folderPreview.files.length > 0 &&
		blockingCollisions.length === 0 &&
		unstorablePaths.length === 0 &&
		(unreadEntries.length === 0 || failuresAcknowledged);

	const confirmFolder = () => {
		if (!folderPreview || !canConfirmFolder) return;
		const files = folderPreview.files;
		setFolderPreview(null);
		setFailuresAcknowledged(false);
		onFilesSelected(files);
	};

	return (
		<>
			{/* File Selection State */}
			<div
				className="p-6 flex"
				style={{
					justifyContent: "space-between",
					flexDirection: "column",
					minWidth: "559px",
					minHeight: "400px",
				}}
			>
				{/* Title */}
				<div
					className="text-left mb"
					style={{
						fontSize: "18px",
						lineHeight: "22px",
						fontWeight: "500",
						color: Colors.TEXT_PRIMARY,
					}}
				>
					Upload Files & Folders
				</div>
				<div
					className="text-sm text-gray-400 mb-2"
					style={{ color: Colors.TEXT_SECONDARY }}
				>
					Upload files or a whole folder. You can add more to a deal any time
					later.
				</div>

				{/* Drag and drop area */}
				<div
					className="w-full h-[215px] rounded-[10px] border border-dashed flex flex-col items-center justify-center transition-colors mb-4"
					style={{
						backgroundColor: isDragOver
							? Colors.PRIMARY_LIGHT
							: Colors.PRIMARY_BG,
						borderColor: isDragOver
							? Colors.PRIMARY_MEDIUM
							: Colors.PRIMARY_BORDER,
					}}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					{folderPreview ? (
						<div className="flex flex-col items-center px-6 text-center">
							<FolderTree
								className="mb-3 h-8 w-8 text-[#008782]"
								strokeWidth={1.5}
								aria-hidden
							/>
							<div className="text-base font-medium text-text-strong">
								{folderPreview.files.length}{" "}
								{folderPreview.files.length === 1 ? "file" : "files"} from{" "}
								{folderPreview.folderName || "the selected folder"}
							</div>
							<div className="mt-1.5 text-sm text-text-muted">
								Sub-folders are preserved under your project name.
							</div>
							{(folderPreview.skippedJunk > 0 ||
								folderPreview.skippedUnsupported > 0) && (
								<div className="mt-1 text-xs text-text-muted">
									{folderPreview.skippedJunk > 0 &&
										`${folderPreview.skippedJunk} system file${
											folderPreview.skippedJunk === 1 ? "" : "s"
										} skipped`}
									{folderPreview.skippedJunk > 0 &&
										folderPreview.skippedUnsupported > 0 &&
										" · "}
									{folderPreview.skippedUnsupported > 0 &&
										`${folderPreview.skippedUnsupported} unsupported file${
											folderPreview.skippedUnsupported === 1 ? "" : "s"
										} skipped`}
								</div>
							)}
							{blockingCollisions.length > 0 && (
								<div className="mt-3 w-full rounded-md bg-red-50 px-3 py-2 text-left">
									<div className="text-xs font-medium text-red-700">
										{blockingCollisions.length} duplicate path
										{blockingCollisions.length === 1 ? "" : "s"} — these files
										would overwrite each other:
									</div>
									<ul className="mt-1 max-h-16 overflow-y-auto text-xs text-red-700">
										{blockingCollisions.slice(0, 5).map((collision) => (
											<li key={collision.relativePath} className="truncate">
												{collision.relativePath} ({collision.names.length}{" "}
												files)
											</li>
										))}
									</ul>
								</div>
							)}

							{unstorablePaths.length > 0 && (
								<div className="mt-3 w-full rounded-md bg-red-50 px-3 py-2 text-left">
									<div className="text-xs font-medium text-red-700">
										{unstorablePaths.length} file
										{unstorablePaths.length === 1 ? "" : "s"} cannot be stored
										under {unstorablePaths.length === 1 ? "its" : "their"} path
										— rename {unstorablePaths.length === 1 ? "it" : "them"} and
										try again:
									</div>
									<ul className="mt-1 max-h-16 overflow-y-auto text-xs text-red-700">
										{unstorablePaths.slice(0, 5).map((entry) => (
											<li key={entry.relativePath} className="truncate">
												{entry.relativePath} — {entry.problems.join("; ")}
											</li>
										))}
									</ul>
								</div>
							)}

							{unreadEntries.length > 0 && (
								<div className="mt-3 w-full rounded-md bg-amber-50 px-3 py-2 text-left">
									<div className="text-xs font-medium text-amber-800">
										{unreadEntries.length} item
										{unreadEntries.length === 1 ? "" : "s"} could not be read
										and will not be uploaded:
									</div>
									<ul className="mt-1 max-h-16 overflow-y-auto text-xs text-amber-800">
										{unreadEntries.slice(0, 5).map((entry) => (
											<li
												key={`${entry.path}:${entry.reason}`}
												className="truncate"
											>
												{entry.path} — {entry.reason}
											</li>
										))}
									</ul>
									<label className="mt-2 flex items-center gap-2 text-xs text-amber-900">
										<input
											type="checkbox"
											checked={failuresAcknowledged}
											onChange={(e) =>
												setFailuresAcknowledged(e.target.checked)
											}
										/>
										Upload anyway, without these items
									</label>
								</div>
							)}

							<button
								onClick={() => setFolderPreview(null)}
								className="mt-3 text-xs font-medium text-text-muted underline"
							>
								Choose something else
							</button>
						</div>
					) : (
						<>
					{/* Upload icon */}
					<img
						className="mb-2"
						src={uploadIcon}
						alt="Upload"
						style={{ marginTop: "10px", height: "50px", width: "107px" }}
					/>

					<div
						className="text-center mb"
						style={{
							fontSize: "14px",
							lineHeight: "25px",
							color: Colors.TEXT_SECONDARY,
							whiteSpace: "pre-wrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							maxWidth: "50%",
						}}
					>
						Drag and drop your deal room folder, or click to browse and select
						your files
					</div>

					<div
						className="text-center"
						style={{
							fontSize: "12px",
							lineHeight: "25px",
							color: Colors.PRIMARY,
							marginTop: "34px",
							flexDirection: "row",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<img
							src={supportedFileUploadIcon}
							alt="logo"
							style={{ width: "11px", height: "11px", marginRight: "4px" }}
						/>
						<span className="text-gray-500 text-xs text-left mr-4">
							Supports all file types
						</span>
						<img
							src={noSizeLimitsIcon}
							alt="logo"
							style={{ width: "11px", height: "11px", marginRight: "4px" }}
						/>
						<span className="text-gray-500 text-xs text-left mr-4">
							No size limits
						</span>
						<img
							src={folderStructureIcon}
							alt="logo"
							style={{ width: "11px", height: "11px", marginRight: "4px" }}
						/>
						<span className="text-gray-500 text-xs text-left mr-4">
							Folder structure preserved
						</span>
					</div>
						</>
					)}
				</div>

				{/* Upload buttons */}
				{folderPreview ? (
					<button
						onClick={confirmFolder}
						disabled={!canConfirmFolder}
						className="w-full h-[37px] rounded-md flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
						style={{ backgroundColor: Colors.PRIMARY }}
					>
						<span
							className="text-sm font-medium"
							style={{ color: Colors.WHITE }}
						>
							Upload {folderPreview.files.length}{" "}
							{folderPreview.files.length === 1 ? "file" : "files"}
						</span>
						<img
							src={uploadFileIcon}
							alt="Upload"
							style={{ width: "12px", height: "12px" }}
						/>
					</button>
				) : (
					<div className="flex gap-3">
						<button
							onClick={handleUploadClick}
							className="flex-1 h-[37px] rounded-md flex items-center justify-center gap-2"
							style={{ backgroundColor: Colors.PRIMARY }}
						>
							<span
								className="text-sm font-medium"
								style={{ color: Colors.WHITE }}
							>
								Select Files
							</span>
							<img
								src={uploadFileIcon}
								alt="Upload"
								style={{ width: "12px", height: "12px" }}
							/>
						</button>
						<button
							onClick={handleFolderClick}
							className="flex-1 h-[37px] rounded-md flex items-center justify-center gap-2 border border-gray-300 bg-white hover:bg-gray-50"
						>
							<span className="text-sm font-medium text-[#4B5563]">
								Upload Folder
							</span>
							<FolderTree
								className="h-3.5 w-3.5 text-[#4B5563]"
								strokeWidth={1.75}
								aria-hidden
							/>
						</button>
					</div>
				)}
			</div>

			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				multiple
				accept=".png,.jpg,.jpeg,.pdf,.xlsx,.xls,.doc,.docx,.md,.markdown,.txt,.zip"
				onChange={handleFileInputChange}
				className="hidden"
			/>

			{/* Hidden folder input — `webkitdirectory` is attached in an effect */}
			<input
				ref={folderInputRef}
				type="file"
				multiple
				data-testid="folder-input"
				onChange={handleFolderInputChange}
				className="hidden"
			/>
		</>
	);
};

export default FileSelection;
